import moment from 'moment-timezone';
import { formatWeekDay } from '@mummssoftware/utils/formatters';
import { getStartDate } from '@mummssoftware/utils/utils';
import { DoneCellRenderer } from './CellRenderer';
import { Completed, Refused } from '../Context/constants';

/**
 * Cell Renderer
 * get past week days
 */
const getPastWeek = (startDayDate: moment.Moment) => {
  const weekDays = [startDayDate];
  for (let i = 1; i < 6; i++) {
    const previousDay = moment(startDayDate).subtract(i, 'days');

    weekDays.unshift(previousDay);
  }

  return weekDays;
};
/**
 * Cell Renderer
 * get date columns
 * startDate is in UTC
 */
export const getDateColumns = (startDate: string) => {
  const startDayDate = getStartDate(startDate);
  const weekdays: moment.Moment[] = getPastWeek(startDayDate);
  const columnDef = {
    headerName: '',
    field: 'week-date',
    cellClass: 'no-focus-outline',
    suppressMenu: true,
    cellRendererFramework: DoneCellRenderer,
    width: 50,
  };
  const defs = weekdays.flatMap((date: moment.Moment) => {
    // localStartDate in in local format because we are displaying local dates in headers
    const localStartDate = moment.utc(date).local();
    const headerDate = formatWeekDay(localStartDate.toDate());
    // moment weekday 0 is SUNDAY
    const isSunday = date.weekday() === 0;

    if (isSunday) {
      // add a column between saturday and sunday
      return [
        {
          headerName: '',
          field: 'week-date',
          cellClass: 'no-focus-outline task-disabled',
          suppressMenu: true,
          width: 10,
          cellRendererFramework: DoneCellRenderer,
          // cellStyle: { 'background-color': 'black' },
        },
        {
          ...columnDef,
          cellClass: 'no-focus-outline',
          headerName: headerDate,
          cellRendererParams: {
            dateColumn: date,
          },
          // cellStyle: { 'background-color': 'darkgrey' },
        },
      ];
    }
    return {
      ...columnDef,
      cellClass: 'no-focus-outline',
      headerName: formatWeekDay(date.toDate()),
      cellRendererParams: {
        dateColumn: date,
      },
    };
  });

  return defs;
};

/**
 * In order to make sure we compare date in the same format
 * task.patientvisitdatetime is in UTC format
 * startDate is in UTC format
 */
// todo check if startDate can be in Moment now
export const tasksCompleted = (
  data: any,
  // tasksCompleted used for moment Dates and string dates
  // todo harmonize
  startDate: moment.Moment | string,
  // if taskStatus given filter on it, otherwise both Refused and Completed
  taskStatus?: string,
) => {
  // @note both startDate and task patientvisitdatetime are in UCT time
  const hasTasks =
    data.patientCppTaskAdministrations &&
    data.patientCppTaskAdministrations.length;
  let completedTasks: any = null;
  // filter on it or not
  if (taskStatus) {
    completedTasks = hasTasks
      ? data.patientCppTaskAdministrations.filter(
          (task: mumms.AdminSubTask) => task.status === taskStatus,
        )
      : null;
  } else {
    completedTasks = hasTasks
      ? data.patientCppTaskAdministrations.filter(
          (task: mumms.AdminSubTask) =>
            task.status === Refused || task.status === Completed,
        )
      : null;
  }
  const hasCompletedTasks = completedTasks ? completedTasks?.length : null;
  // returns a boolean or a task
  let isTaskDone: any | mumms.AdminSubTask[] | boolean = false;
  if (hasCompletedTasks) {
    const completedTasksFormatted = completedTasks.map(
      (task: mumms.AdminSubTask) => ({
        ...task,
        patientvisitdatetime: getStartDate(task.patientvisitdatetime),
      }),
    );
    const isDateNotFormatted = typeof startDate === 'string';
    const visitDate: any = isDateNotFormatted
      ? getStartDate(startDate as string)
      : startDate;
    const tasksDoneFound = completedTasksFormatted.filter((task: any) =>
      task.patientvisitdatetime.isSame(visitDate),
    );
    isTaskDone = tasksDoneFound;
  }
  return isTaskDone;
};

/**
 * isTaskCompleted on the same visit ID
 */
export const isTaskCompleted = (
  data: any,
  startDate: moment.Moment | string,
  taskStatus: string,
  visitid?: number | string,
) => {
  const isTaskDone = tasksCompleted(data, startDate, taskStatus);
  const taskDone =
    isTaskDone && isTaskDone.length ? isTaskDone[isTaskDone.length - 1] : false;
  const sameVisitTask =
    taskDone && taskDone.patientvisit_id === visitid?.toString()
      ? taskDone
      : false;
  return sameVisitTask;
};
/**
 * isTaskTicked both refused and completed
 */
export const isTaskTicked = (
  data: any,
  startDate: moment.Moment | string,
  visitid?: number | string,
) => {
  const isTaskDone = tasksCompleted(data, startDate);
  const taskDone =
    isTaskDone && isTaskDone.length ? isTaskDone[isTaskDone.length - 1] : false;
  const sameVisitTask =
    taskDone && taskDone.patientvisit_id === visitid?.toString()
      ? taskDone
      : false;
  return sameVisitTask;
};
